import { apiFetch } from "@pontimed/api-client";

/**
 * Signup profile-completion plumbing, shared by `auth-modal.tsx` (the step
 * itself) and `auth-modal-context.tsx` (the OAuth / magic-link resume).
 *
 * WHY THIS EXISTS
 * ---------------
 * Every sign-in path (phone OTP, email OTP, magic link, OAuth) creates a
 * `User` row but NEVER a `Profile` row — see apps/api
 * `packages/auth-server/src/otp/phone.ts`, `routes/auth-email.ts` and
 * `routes/auth-oauth.ts`. `POST /api/appointments` resolves the patient from
 * `User.profileId` and rejects the booking when it is null (same for waitlist
 * joins + family members), so a brand-new patient can't book anything until a
 * Profile exists.
 *
 * The legacy Next modal (`src/components/auth/auth-modal.tsx`) closed that gap
 * with a "complete profile" step after the first successful verification,
 * POSTing `/api/auth/complete-profile`. This module restores the same
 * decision logic against apps/api.
 */

/**
 * Shape of `GET /api/auth/profile` (apps/api `routes/auth-misc.ts`) — the same
 * endpoint the legacy modal probed after a verification to tell "returning
 * user" from "brand-new signup".
 */
export interface AuthProfileSnapshot {
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  phoneVerified: boolean;
  userType: string;
  providerId: string | null;
  profileId: string | null;
}

/**
 * userTypes we may complete with `role: "user"`. The apps/api handler writes
 * `userType: "user"` alongside the new `profileId`, so firing it for a
 * provider / facility_admin / admin_editor would DOWNGRADE their account.
 * Those roles get their profile from their own onboarding flow instead.
 */
const PATIENT_USER_TYPES = new Set(["user", "user_plus", "patient"]);

/**
 * Placeholder addresses minted when a user signs up without a real email
 * (`<phone>@phone.pontimed.app` from phone-only signup,
 * `<provider>-<sub>@oauth.pontimed.app` from Apple private relay). They are
 * storage discriminators, never a contact — treat them as "no email yet".
 */
export function isSyntheticEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.endsWith("@phone.pontimed.app") || email.endsWith("@oauth.pontimed.app");
}

/**
 * D-R4 (`_docs/marketing-automation-spec.md` §7): whether the profile step may
 * offer the "news and tips by email" opt-in. Only when the ACCOUNT carries a
 * real address — a phone-only signup or an OAuth login that returned no email
 * has only a synthetic placeholder (above), and apps/api records the consent
 * against the account's real address or not at all. An address typed into
 * the step's optional email field doesn't count: it is unverified and never
 * becomes the account's email.
 */
export function canOfferMarketingOptIn(
  snapshot: Pick<AuthProfileSnapshot, "email" | "emailVerified">,
): boolean {
  // Same rule the API applies before recording the consent: a real address
  // the person has proved they own. An unverified one could be a typo.
  return !!snapshot.email && snapshot.emailVerified && !isSyntheticEmail(snapshot.email);
}

/** Reads the freshly-authenticated identity. `null` on any failure — callers
 *  must fall back to "let the user through", never trap them behind a step we
 *  couldn't justify. */
export async function fetchAuthProfile(): Promise<AuthProfileSnapshot | null> {
  try {
    return await apiFetch.get<AuthProfileSnapshot>("/api/auth/profile");
  } catch {
    return null;
  }
}

/**
 * Legacy parity: skip the step when the user already has a personal profile OR
 * a provider profile (`profile.profileId || profile.providerId` in the old
 * modal). Plus the userType guard above.
 */
export function needsProfileCompletion(snapshot: AuthProfileSnapshot): boolean {
  if (snapshot.profileId || snapshot.providerId) return false;
  return PATIENT_USER_TYPES.has(snapshot.userType);
}

/* ─────────────── Round-trip resume marker ─────────────── */

/**
 * OAuth and magic-link sign-ins leave the SPA entirely (provider consent page /
 * mail client), so the modal is long unmounted when the user comes back and no
 * in-modal step can run. Legacy solved this with a `sessionStorage` intent that
 * `AuthModalProvider` picked up on return (`storeAuthIntent`).
 *
 * We use `localStorage` + a TTL instead of `sessionStorage` for one reason:
 * a magic link opened from a mail client lands in a NEW TAB, where
 * sessionStorage is empty and the legacy resume silently never fired.
 */
const RESUME_KEY = "pontimed_profile_resume";
const RESUME_TTL_MS = 60 * 60 * 1000; // 1h — longer than any realistic round-trip

/** Call right before handing the browser to an external auth round-trip. */
export function markProfileCompletionPending(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RESUME_KEY, String(Date.now()));
  } catch {
    /* Safari private mode — resume just won't fire */
  }
}

/** Drop the marker without acting on it — used when the in-modal step already
 *  owns this signup, so the resume can't fire a second prompt on top. */
export function clearProfileCompletionMarker(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(RESUME_KEY);
  } catch {
    /* ignore */
  }
}

/** Reads AND clears the marker. Returns true only for a fresh, unexpired one,
 *  so a resume is attempted at most once per round-trip (no prompt loop). */
export function consumeProfileCompletionMarker(): boolean {
  if (typeof window === "undefined") return false;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(RESUME_KEY);
    if (raw !== null) window.localStorage.removeItem(RESUME_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < RESUME_TTL_MS;
}
